
import { config } from '../config/config';

let didWarnMissingFmpApiKey = false;

const AUTH_STORAGE_KEY = 'garp_admin_auth_v1';

type LocalFinancialRow = Record<string, string>;
type LocalFinancialData = {
  annual: LocalFinancialRow[];
  quarterly: LocalFinancialRow[];
};

const MILLION = 1_000_000;

function parseLocalNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed || trimmed === '-') return null;

  const normalized = trimmed.replace(/,/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function dollarsFromMillions(value: unknown): number {
  return (parseLocalNumber(value) ?? 0) * MILLION;
}

function ratioFromPercent(value: unknown): number {
  return (parseLocalNumber(value) ?? 0) / 100;
}

function fiscalPeriodToIsoDate(value: unknown): string {
  // Input looks like "2010-09" (YYYY-MM). We return the last day of that month in UTC.
  if (typeof value !== 'string') return '';
  const [yRaw, mRaw] = value.split('-');
  const year = Number(yRaw);
  const month = Number(mRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return '';

  // JS months are 0-based; day 0 gives last day of previous month.
  const dt = new Date(Date.UTC(year, month, 0));
  return dt.toISOString().slice(0, 10);
}

function sortDescByFiscalPeriod(rows: LocalFinancialRow[]): LocalFinancialRow[] {
  return [...rows].sort((a, b) => {
    const aDate = fiscalPeriodToIsoDate(a?.['Fiscal Period']);
    const bDate = fiscalPeriodToIsoDate(b?.['Fiscal Period']);
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });
}

function makeIncomeStatementBase(symbol: string, date: string, period: string): IncomeStatement {
  const fiscalYear = date ? String(new Date(date).getUTCFullYear()) : '';
  return {
    date,
    symbol,
    reportedCurrency: 'USD',
    cik: '',
    filingDate: '',
    acceptedDate: '',
    fiscalYear,
    period,
    revenue: 0,
    costOfRevenue: 0,
    grossProfit: 0,
    researchAndDevelopmentExpenses: 0,
    generalAndAdministrativeExpenses: 0,
    sellingAndMarketingExpenses: 0,
    sellingGeneralAndAdministrativeExpenses: 0,
    otherExpenses: 0,
    operatingExpenses: 0,
    costAndExpenses: 0,
    netInterestIncome: 0,
    interestIncome: 0,
    interestExpense: 0,
    depreciationAndAmortization: 0,
    ebitda: 0,
    ebit: 0,
    nonOperatingIncomeExcludingInterest: 0,
    operatingIncome: 0,
    totalOtherIncomeExpensesNet: 0,
    incomeBeforeTax: 0,
    incomeTaxExpense: 0,
    netIncomeFromContinuingOperations: 0,
    netIncomeFromDiscontinuedOperations: 0,
    otherAdjustmentsToNetIncome: 0,
    netIncome: 0,
    netIncomeDeductions: 0,
    bottomLineNetIncome: 0,
    eps: 0,
    epsDiluted: 0,
    weightedAverageShsOut: 0,
    weightedAverageShsOutDil: 0,
  };
}

function mapLocalRowToIncomeStatement(row: LocalFinancialRow, symbol: string, period: 'annual' | 'quarter'): IncomeStatement {
  const date = fiscalPeriodToIsoDate(row?.['Fiscal Period']);
  const statement = makeIncomeStatementBase(symbol, date, period === 'annual' ? 'FY' : 'Q');

  const revenue = dollarsFromMillions(row?.['Revenue']);
  const grossProfit = dollarsFromMillions(row?.['Gross Profit']);
  const operatingIncome = dollarsFromMillions(row?.['Operating Income']);
  const pretax = dollarsFromMillions(row?.['Pretax Income']);
  const netIncome = dollarsFromMillions(row?.['Net Income']);
  const sharesOutDil = (parseLocalNumber(row?.['Shares Outstanding (Diluted Average)']) ?? 0) * MILLION;

  // Derivations when data is missing.
  const costOfRevenue = Math.max(0, revenue - grossProfit);
  const operatingExpenses = Math.max(0, grossProfit - operatingIncome);
  const incomeTaxExpense = pretax && pretax !== 0 ? Math.max(0, pretax - netIncome) : 0;

  const epsDiluted = parseLocalNumber(row?.['EPS (Diluted)']) ?? (sharesOutDil > 0 ? netIncome / sharesOutDil : 0);

  statement.revenue = revenue;
  statement.grossProfit = grossProfit;
  statement.operatingIncome = operatingIncome;
  statement.incomeBeforeTax = pretax;
  statement.netIncome = netIncome;
  statement.bottomLineNetIncome = netIncome;
  statement.costOfRevenue = costOfRevenue;
  statement.operatingExpenses = operatingExpenses;
  statement.costAndExpenses = costOfRevenue + operatingExpenses;
  statement.incomeTaxExpense = incomeTaxExpense;
  statement.epsDiluted = epsDiluted;
  statement.eps = epsDiluted;
  statement.weightedAverageShsOutDil = sharesOutDil;
  statement.weightedAverageShsOut = sharesOutDil;

  return statement;
}

function makeBalanceSheetBase(symbol: string, date: string, period: string): BalanceSheet {
  const fiscalYear = date ? String(new Date(date).getUTCFullYear()) : '';
  return {
    date,
    symbol,
    reportedCurrency: 'USD',
    cik: '',
    filingDate: '',
    acceptedDate: '',
    fiscalYear,
    period,
    cashAndCashEquivalents: 0,
    shortTermInvestments: 0,
    cashAndShortTermInvestments: 0,
    netReceivables: 0,
    inventory: 0,
    otherCurrentAssets: 0,
    totalCurrentAssets: 0,
    propertyPlantEquipmentNet: 0,
    goodwill: 0,
    intangibleAssets: 0,
    goodwillAndIntangibleAssets: 0,
    longTermInvestments: 0,
    taxAssets: 0,
    otherNonCurrentAssets: 0,
    totalNonCurrentAssets: 0,
    otherAssets: 0,
    totalAssets: 0,
    accountPayables: 0,
    otherPayables: 0,
    accruedExpenses: 0,
    shortTermDebt: 0,
    taxPayables: 0,
    deferredRevenue: 0,
    otherCurrentLiabilities: 0,
    totalCurrentLiabilities: 0,
    longTermDebt: 0,
    capitalLeaseObligationsNonCurrent: 0,
    deferredRevenueNonCurrent: 0,
    deferredTaxLiabilitiesNonCurrent: 0,
    otherNonCurrentLiabilities: 0,
    totalNonCurrentLiabilities: 0,
    otherLiabilities: 0,
    capitalLeaseObligations: 0,
    totalLiabilities: 0,
    treasuryStock: 0,
    preferredStock: 0,
    commonStock: 0,
    retainedEarnings: 0,
    additionalPaidInCapital: 0,
    accumulatedOtherComprehensiveIncomeLoss: 0,
    otherTotalStockholdersEquity: 0,
    othertotalStockholdersEquity: 0,
    totalStockholdersEquity: 0,
    totalEquity: 0,
    totalLiabilitiesAndStockholdersEquity: 0,
    minorityInterest: 0,
    totalLiabilitiesAndTotalEquity: 0,
    totalInvestments: 0,
    totalDebt: 0,
    netDebt: 0,
  };
}

function mapLocalRowToBalanceSheet(row: LocalFinancialRow, symbol: string, period: 'annual' | 'quarter'): BalanceSheet {
  const date = fiscalPeriodToIsoDate(row?.['Fiscal Period']);
  const bs = makeBalanceSheetBase(symbol, date, period === 'annual' ? 'FY' : 'Q');

  const totalAssets = dollarsFromMillions(row?.['Total Assets']);
  const totalLiabilities = dollarsFromMillions(row?.['Total Liabilities']);
  const equity = dollarsFromMillions(row?.['Total Stockholders Equity']);
  const shortTermDebt = row?.['Short-Term Debt'];
  const longTermDebt = row?.['Long-Term Debt'];
  const shortDebtVal = dollarsFromMillions(shortTermDebt);
  const longDebtVal = dollarsFromMillions(longTermDebt);
  const totalDebt = shortDebtVal + longDebtVal;

  bs.totalAssets = totalAssets;
  bs.totalLiabilities = totalLiabilities;
  bs.totalStockholdersEquity = equity;
  bs.totalEquity = equity;
  bs.shortTermDebt = shortDebtVal;
  bs.longTermDebt = longDebtVal;
  bs.totalDebt = totalDebt;
  bs.netDebt = totalDebt;
  bs.totalLiabilitiesAndStockholdersEquity = totalLiabilities + equity;
  bs.totalLiabilitiesAndTotalEquity = totalLiabilities + equity;
  bs.totalLiabilitiesAndStockholdersEquity = totalLiabilities + equity;
  bs.totalLiabilitiesAndTotalEquity = totalLiabilities + equity;
  bs.totalLiabilitiesAndTotalEquity = totalLiabilities + equity;
  (bs as any).totalLiabilitiesAndTotalEquity = totalLiabilities + equity;
  (bs as any).totalLiabilitiesAndStockholdersEquity = totalLiabilities + equity;
  bs.totalLiabilitiesAndStockholdersEquity = totalLiabilities + equity;

  return bs;
}

function makeCashFlowBase(symbol: string, date: string, period: string): CashFlowStatement {
  const fiscalYear = date ? String(new Date(date).getUTCFullYear()) : '';
  return {
    date,
    symbol,
    reportedCurrency: 'USD',
    cik: '',
    filingDate: '',
    acceptedDate: '',
    fiscalYear,
    period,
    // Local financial JSON doesn't include all cash flow line-items.
    // Use NaN for "missing" so screens can render N/A (instead of misleading 0).
    netIncome: Number.NaN,
    depreciationAndAmortization: Number.NaN,
    deferredIncomeTax: Number.NaN,
    stockBasedCompensation: Number.NaN,
    changeInWorkingCapital: Number.NaN,
    accountsReceivables: Number.NaN,
    inventory: Number.NaN,
    accountsPayables: Number.NaN,
    otherWorkingCapital: Number.NaN,
    otherNonCashItems: Number.NaN,
    netCashProvidedByOperatingActivities: Number.NaN,
    investmentsInPropertyPlantAndEquipment: 0,
    acquisitionsNet: Number.NaN,
    purchasesOfInvestments: Number.NaN,
    salesMaturitiesOfInvestments: Number.NaN,
    otherInvestingActivities: Number.NaN,
    netCashUsedForInvestingActivities: Number.NaN,
    debtRepayment: Number.NaN,
    commonStockIssued: Number.NaN,
    commonStockRepurchased: Number.NaN,
    dividendsPaid: Number.NaN,
    netDividendsPaid: Number.NaN,
    commonDividendsPaid: Number.NaN,
    otherFinancingActivities: Number.NaN,
    netCashUsedProvidedByFinancingActivities: Number.NaN,
    effectOfForexChangesOnCash: Number.NaN,
    netChangeInCash: Number.NaN,
    cashAtEndOfPeriod: Number.NaN,
    cashAtBeginningOfPeriod: Number.NaN,
    operatingCashFlow: Number.NaN,
    capitalExpenditure: Number.NaN,
    freeCashFlow: Number.NaN,
  };
}

function mapLocalRowToCashFlow(row: LocalFinancialRow, symbol: string, period: 'annual' | 'quarter'): CashFlowStatement {
  const date = fiscalPeriodToIsoDate(row?.['Fiscal Period']);
  const cf = makeCashFlowBase(symbol, date, period === 'annual' ? 'FY' : 'Q');

  const netIncome = dollarsFromMillions(row?.['Net Income']);
  const operatingCashFlow = dollarsFromMillions(row?.['Cash Flow from Operations']);
  const freeCashFlow = dollarsFromMillions(row?.['Free Cash Flow']);
  const issuance = dollarsFromMillions(row?.['Issuance of Stock']);
  const repurchase = dollarsFromMillions(row?.['Repurchase of Stock']);
  const dividends = dollarsFromMillions(row?.['Cash Flow for Dividends']);

  // FMP convention: capitalExpenditure is usually negative.
  const capex = freeCashFlow - operatingCashFlow;

  cf.netIncome = netIncome;
  cf.operatingCashFlow = operatingCashFlow;
  cf.netCashProvidedByOperatingActivities = operatingCashFlow;
  cf.freeCashFlow = freeCashFlow;
  cf.capitalExpenditure = capex;

  cf.commonStockIssued = issuance;
  cf.commonStockRepurchased = repurchase;
  cf.dividendsPaid = dividends;
  cf.netDividendsPaid = dividends;
  cf.commonDividendsPaid = dividends;

  cf.netCashUsedProvidedByFinancingActivities = issuance + repurchase + dividends;

  return cf;
}

function makeKeyMetricsBase(symbol: string, date: string, period: string): KeyMetrics {
  return {
    date,
    symbol,
    period,
    revenuePerShare: 0,
    netIncomePerShare: 0,
    operatingCashFlowPerShare: 0,
    freeCashFlowPerShare: 0,
    cashPerShare: 0,
    bookValuePerShare: 0,
    tangibleBookValuePerShare: 0,
    shareholdersEquityPerShare: 0,
    interestDebtPerShare: 0,
    marketCap: 0,
    enterpriseValue: 0,
    peRatio: 0,
    priceToSalesRatio: 0,
    pocfratio: 0,
    pfcfRatio: 0,
    pbRatio: 0,
    ptbRatio: 0,
    evToSales: 0,
    enterpriseValueOverEBITDA: 0,
    evToOperatingCashFlow: 0,
    evToFreeCashFlow: 0,
    earningsYield: 0,
    freeCashFlowYield: 0,
    debtToEquity: 0,
    debtToAssets: 0,
    netDebtToEBITDA: 0,
    currentRatio: 0,
    interestCoverage: 0,
    incomeQuality: 0,
    dividendYield: 0,
    payoutRatio: 0,
    salesGeneralAndAdministrativeToRevenue: 0,
    researchAndDdevelopementToRevenue: 0,
    intangiblesToTotalAssets: 0,
    capexToOperatingCashFlow: 0,
    capexToRevenue: 0,
    capexToDepreciation: 0,
    stockBasedCompensationToRevenue: 0,
    grahamNumber: 0,
    roic: 0,
    returnOnInvestedCapital: 0,
    returnOnTangibleAssets: 0,
    grahamNetNet: 0,
    workingCapital: 0,
    tangibleAssetValue: 0,
    netCurrentAssetValue: 0,
    investedCapital: 0,
    averageReceivables: 0,
    averagePayables: 0,
    averageInventory: 0,
    daysSalesOutstanding: 0,
    daysPayablesOutstanding: 0,
    daysOfInventoryOnHand: 0,
    receivablesTurnover: 0,
    payablesTurnover: 0,
    inventoryTurnover: 0,
    roe: 0,
    capexPerShare: 0,
  };
}

function mapLocalRowToKeyMetrics(row: LocalFinancialRow, symbol: string, period: 'annual' | 'quarter'): KeyMetrics {
  const date = fiscalPeriodToIsoDate(row?.['Fiscal Period']);
  const km = makeKeyMetricsBase(symbol, date, period === 'annual' ? 'FY' : 'Q');

  const revenue = dollarsFromMillions(row?.['Revenue']);
  const netIncome = dollarsFromMillions(row?.['Net Income']);
  const operatingCashFlow = dollarsFromMillions(row?.['Cash Flow from Operations']);
  const freeCashFlow = dollarsFromMillions(row?.['Free Cash Flow']);
  const equity = dollarsFromMillions(row?.['Total Stockholders Equity']);
  const assets = dollarsFromMillions(row?.['Total Assets']);
  const shortDebt = dollarsFromMillions(row?.['Short-Term Debt']);
  const longDebt = dollarsFromMillions(row?.['Long-Term Debt']);
  const totalDebt = shortDebt + longDebt;
  const capex = freeCashFlow - operatingCashFlow;
  const sharesOut = (parseLocalNumber(row?.['Shares Outstanding (Diluted Average)']) ?? 0) * MILLION;

  const mockQuote = getMockStockQuote(symbol);
  const price = mockQuote?.price || 0;
  const marketCap = sharesOut > 0 ? price * sharesOut : (mockQuote?.marketCap || 0);

  const safeDiv = (num: number, den: number): number => (den ? num / den : 0);

  km.marketCap = marketCap;
  km.enterpriseValue = marketCap;

  km.revenuePerShare = safeDiv(revenue, sharesOut);
  km.netIncomePerShare = safeDiv(netIncome, sharesOut);
  km.operatingCashFlowPerShare = safeDiv(operatingCashFlow, sharesOut);
  km.freeCashFlowPerShare = safeDiv(freeCashFlow, sharesOut);
  km.capexPerShare = safeDiv(capex, sharesOut);

  km.bookValuePerShare = safeDiv(equity, sharesOut);
  km.shareholdersEquityPerShare = safeDiv(equity, sharesOut);
  km.interestDebtPerShare = safeDiv(totalDebt, sharesOut);

  km.priceToSalesRatio = safeDiv(marketCap, revenue);
  km.peRatio = safeDiv(marketCap, netIncome);
  km.pocfratio = safeDiv(marketCap, operatingCashFlow);
  km.pfcfRatio = safeDiv(marketCap, freeCashFlow);
  km.pbRatio = safeDiv(marketCap, equity);
  km.ptbRatio = km.pbRatio;

  km.earningsYield = km.peRatio ? safeDiv(1, km.peRatio) : 0;
  km.freeCashFlowYield = safeDiv(freeCashFlow, marketCap);

  km.debtToEquity = safeDiv(totalDebt, equity);
  km.debtToAssets = safeDiv(totalDebt, assets);
  km.roe = safeDiv(netIncome, equity);

  const roicRatio = ratioFromPercent(row?.['ROIC %']);
  km.roic = roicRatio;
  km.returnOnInvestedCapital = roicRatio;

  // Provide some basic invested-capital proxy so charts don’t end up NaN.
  km.investedCapital = assets;
  km.workingCapital = 0;

  // Best-effort: capex ratios
  km.capexToOperatingCashFlow = safeDiv(Math.abs(capex), Math.abs(operatingCashFlow));
  km.capexToRevenue = safeDiv(Math.abs(capex), Math.abs(revenue));

  return km;
}

function mapLocalRowToRatios(row: LocalFinancialRow, symbol: string, period: 'annual' | 'quarter'): Ratios {
  const date = fiscalPeriodToIsoDate(row?.['Fiscal Period']);

  const pretax = dollarsFromMillions(row?.['Pretax Income']);
  const netIncome = dollarsFromMillions(row?.['Net Income']);
  const taxExpense = pretax && pretax !== 0 ? Math.max(0, pretax - netIncome) : 0;
  const effectiveTaxRate = pretax ? taxExpense / pretax : undefined;

  return {
    date,
    symbol,
    period: period === 'annual' ? 'FY' : 'Q',
    grossProfitMargin: ratioFromPercent(row?.['Gross Margin %']),
    operatingProfitMargin: ratioFromPercent(row?.['Operating Margin %']),
    netProfitMargin: ratioFromPercent(row?.['Net Margin %']),
    effectiveTaxRate,
  };
}

function computeTtmIncomeFromQuarterly(quarterly: LocalFinancialRow[], symbol: string): IncomeStatement | null {
  const sorted = sortDescByFiscalPeriod(quarterly);
  if (sorted.length < 4) return null;

  const latest4 = sorted.slice(0, 4);
  const date = fiscalPeriodToIsoDate(latest4[0]?.['Fiscal Period']);
  const out = makeIncomeStatementBase(symbol, date, 'TTM');

  const sum = (key: string): number => latest4.reduce((acc, r) => acc + dollarsFromMillions(r?.[key]), 0);
  const revenue = sum('Revenue');
  const grossProfit = sum('Gross Profit');
  const operatingIncome = sum('Operating Income');
  const pretax = sum('Pretax Income');
  const netIncome = sum('Net Income');

  const costOfRevenue = Math.max(0, revenue - grossProfit);
  const operatingExpenses = Math.max(0, grossProfit - operatingIncome);
  const incomeTaxExpense = pretax ? Math.max(0, pretax - netIncome) : 0;

  const sharesOutDil = (parseLocalNumber(latest4[0]?.['Shares Outstanding (Diluted Average)']) ?? 0) * MILLION;
  const epsDiluted = sharesOutDil > 0 ? netIncome / sharesOutDil : 0;

  out.revenue = revenue;
  out.grossProfit = grossProfit;
  out.operatingIncome = operatingIncome;
  out.incomeBeforeTax = pretax;
  out.netIncome = netIncome;
  out.bottomLineNetIncome = netIncome;
  out.costOfRevenue = costOfRevenue;
  out.operatingExpenses = operatingExpenses;
  out.costAndExpenses = costOfRevenue + operatingExpenses;
  out.incomeTaxExpense = incomeTaxExpense;
  out.weightedAverageShsOutDil = sharesOutDil;
  out.weightedAverageShsOut = sharesOutDil;
  out.epsDiluted = epsDiluted;
  out.eps = epsDiluted;

  return out;
}

function computeTtmCashFlowFromQuarterly(quarterly: LocalFinancialRow[], symbol: string): CashFlowStatement | null {
  const sorted = sortDescByFiscalPeriod(quarterly);
  if (sorted.length < 4) return null;

  const latest4 = sorted.slice(0, 4);
  const date = fiscalPeriodToIsoDate(latest4[0]?.['Fiscal Period']);
  const out = makeCashFlowBase(symbol, date, 'TTM');

  const sum = (key: string): number => latest4.reduce((acc, r) => acc + dollarsFromMillions(r?.[key]), 0);
  const netIncome = sum('Net Income');
  const operatingCashFlow = sum('Cash Flow from Operations');
  const freeCashFlow = sum('Free Cash Flow');
  const issuance = sum('Issuance of Stock');
  const repurchase = sum('Repurchase of Stock');
  const dividends = sum('Cash Flow for Dividends');

  const capex = freeCashFlow - operatingCashFlow;

  out.netIncome = netIncome;
  out.operatingCashFlow = operatingCashFlow;
  out.netCashProvidedByOperatingActivities = operatingCashFlow;
  out.freeCashFlow = freeCashFlow;
  out.capitalExpenditure = capex;

  out.commonStockIssued = issuance;
  out.commonStockRepurchased = repurchase;
  out.dividendsPaid = dividends;
  out.netDividendsPaid = dividends;
  out.commonDividendsPaid = dividends;
  out.netCashUsedProvidedByFinancingActivities = issuance + repurchase + dividends;

  return out;
}

function computeTtmIncomeSeriesFromQuarterly(quarterly: LocalFinancialRow[], symbol: string, limit: number): IncomeStatement[] {
  const sortedAsc = [...quarterly]
    .filter((r) => typeof r?.['Fiscal Period'] === 'string')
    .sort((a, b) => new Date(fiscalPeriodToIsoDate(a['Fiscal Period'])).getTime() - new Date(fiscalPeriodToIsoDate(b['Fiscal Period'])).getTime());

  const series: IncomeStatement[] = [];
  for (let i = 3; i < sortedAsc.length; i++) {
    const window = sortedAsc.slice(i - 3, i + 1);
    const date = fiscalPeriodToIsoDate(sortedAsc[i]?.['Fiscal Period']);
    const row = makeIncomeStatementBase(symbol, date, 'TTM');

    const sum = (key: string): number => window.reduce((acc, r) => acc + dollarsFromMillions(r?.[key]), 0);
    const revenue = sum('Revenue');
    const grossProfit = sum('Gross Profit');
    const operatingIncome = sum('Operating Income');
    const pretax = sum('Pretax Income');
    const netIncome = sum('Net Income');

    const costOfRevenue = Math.max(0, revenue - grossProfit);
    const operatingExpenses = Math.max(0, grossProfit - operatingIncome);
    const incomeTaxExpense = pretax ? Math.max(0, pretax - netIncome) : 0;

    const sharesOutDil = (parseLocalNumber(sortedAsc[i]?.['Shares Outstanding (Diluted Average)']) ?? 0) * MILLION;
    const epsDiluted = sharesOutDil > 0 ? netIncome / sharesOutDil : 0;

    row.revenue = revenue;
    row.grossProfit = grossProfit;
    row.operatingIncome = operatingIncome;
    row.incomeBeforeTax = pretax;
    row.netIncome = netIncome;
    row.bottomLineNetIncome = netIncome;
    row.costOfRevenue = costOfRevenue;
    row.operatingExpenses = operatingExpenses;
    row.costAndExpenses = costOfRevenue + operatingExpenses;
    row.incomeTaxExpense = incomeTaxExpense;
    row.weightedAverageShsOutDil = sharesOutDil;
    row.weightedAverageShsOut = sharesOutDil;
    row.epsDiluted = epsDiluted;
    row.eps = epsDiluted;

    series.push(row);
  }

  return limit > 0 ? series.slice(Math.max(0, series.length - limit)) : series;
}

function computeTtmCashFlowSeriesFromQuarterly(quarterly: LocalFinancialRow[], symbol: string, limit: number): CashFlowStatement[] {
  const sortedAsc = [...quarterly]
    .filter((r) => typeof r?.['Fiscal Period'] === 'string')
    .sort((a, b) => new Date(fiscalPeriodToIsoDate(a['Fiscal Period'])).getTime() - new Date(fiscalPeriodToIsoDate(b['Fiscal Period'])).getTime());

  const series: CashFlowStatement[] = [];
  for (let i = 3; i < sortedAsc.length; i++) {
    const window = sortedAsc.slice(i - 3, i + 1);
    const date = fiscalPeriodToIsoDate(sortedAsc[i]?.['Fiscal Period']);
    const row = makeCashFlowBase(symbol, date, 'TTM');

    const sum = (key: string): number => window.reduce((acc, r) => acc + dollarsFromMillions(r?.[key]), 0);
    const netIncome = sum('Net Income');
    const operatingCashFlow = sum('Cash Flow from Operations');
    const freeCashFlow = sum('Free Cash Flow');
    const issuance = sum('Issuance of Stock');
    const repurchase = sum('Repurchase of Stock');
    const dividends = sum('Cash Flow for Dividends');
    const capex = freeCashFlow - operatingCashFlow;

    row.netIncome = netIncome;
    row.operatingCashFlow = operatingCashFlow;
    row.netCashProvidedByOperatingActivities = operatingCashFlow;
    row.freeCashFlow = freeCashFlow;
    row.capitalExpenditure = capex;
    row.commonStockIssued = issuance;
    row.commonStockRepurchased = repurchase;
    row.dividendsPaid = dividends;
    row.netDividendsPaid = dividends;
    row.commonDividendsPaid = dividends;
    row.netCashUsedProvidedByFinancingActivities = issuance + repurchase + dividends;

    series.push(row);
  }

  return limit > 0 ? series.slice(Math.max(0, series.length - limit)) : series;
}

function getBackendAuthTokenFromStorage(): string {
  try {
    if (typeof window === 'undefined') return '';
    const raw = window.localStorage?.getItem(AUTH_STORAGE_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw) as { token?: unknown; expiresAtUtc?: unknown };
    const token = typeof parsed?.token === 'string' ? parsed.token.trim() : '';
    if (!token) return '';

    // Best-effort: drop expired tokens so we don't spam the API.
    const expiresAtUtc = typeof parsed?.expiresAtUtc === 'string' ? parsed.expiresAtUtc : '';
    const expiresMs = expiresAtUtc ? new Date(expiresAtUtc).getTime() : NaN;
    if (Number.isFinite(expiresMs) && expiresMs <= Date.now()) return '';

    return token;
  } catch {
    return '';
  }
}

function canUseBackendFmpProxy(): boolean {
  if (!config.useRealAPI) return false;
  const base = (config.backend?.baseUrl ?? '').trim();
  if (!base) return false;
  return getBackendAuthTokenFromStorage().length > 0;
}

type FmpGetRequest = { url: string; init?: RequestInit };

function makeFmpGetRequest(
  path: string,
  query: Record<string, string | number | boolean | null | undefined>,
  _apiKey: string
): FmpGetRequest | null {
  if (!config.useRealAPI) return null;
  void _apiKey;

  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v == null) continue;
    const sv = String(v);
    if (!sv) continue;
    qs.set(k, sv);
  }

  const base = (config.backend?.baseUrl ?? '').trim().replace(/\/$/, '');
  if (!base) return null;

  const normalizedPath = String(path || '').trim();
  const isPublicSearchPath = /^search/i.test(normalizedPath);
  const isPublicDataPath = /^(quote|batch-quote|historical-price-eod\/full|income-statement|key-metrics|balance-sheet|balance-sheet-statement)$/i.test(normalizedPath);
  const querySuffix = qs.toString() ? `?${qs.toString()}` : '';

  if (canUseBackendFmpProxy()) {
    const token = getBackendAuthTokenFromStorage();
    const url = `${base}/fmp/${path}${querySuffix}`;
    return {
      url,
      init: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    };
  }

  if (isPublicSearchPath || isPublicDataPath) {
    return {
      url: `${base}/fmp/${path}${querySuffix}`,
    };
  }

  // The public Digital Gift screen supplies its dedicated runtime FMP key.
  // Use it directly when no authenticated backend session is available.
  if (String(_apiKey || '').trim()) {
    const directBase = (config.api.baseUrl || 'https://financialmodelingprep.com/stable').replace(/\/$/, '');
    const directQuery = qs.toString()
      ? `${qs.toString()}&apikey=${encodeURIComponent(_apiKey)}`
      : `apikey=${encodeURIComponent(_apiKey)}`;
    return { url: `${directBase}/${path}?${directQuery}` };
  }

  return null;
}

type AnalystEstimateApiRow = {
  symbol: string;
  date: string;
  revenueLow?: number;
  revenueHigh?: number;
  revenueAvg?: number;
  netIncomeLow?: number;
  netIncomeHigh?: number;
  netIncomeAvg?: number;
  epsLow?: number;
  epsHigh?: number;
  epsAvg?: number;
};

// Analyst Estimates API response type
export interface AnalystEstimate {
  symbol: string;
  date: string; // Fiscal year end, e.g. '2025-12-31'
  estimatedRevenue?: number;
  estimatedNetIncome?: number;
  estimatedEps?: number;
  estimatedRevenueLow?: number;
  estimatedRevenueHigh?: number;
  estimatedNetIncomeLow?: number;
  estimatedNetIncomeHigh?: number;
  estimatedEpsLow?: number;
  estimatedEpsHigh?: number;
}

/**
 * Fetch analyst estimates from FMP API
 * @param symbol - Stock symbol
 * @param period - 'annual' or 'quarter' (default: 'annual')
 * @param limit - Number of periods to fetch (default: 3)
 * @param apiKey - API key
 * @returns Promise with analyst estimates array
 */
export async function fetchAnalystEstimates(
  symbol: string = config.api.defaultSymbol,
  period: 'annual' | 'quarter' = 'annual',
  limit: number = 10,
  apiKey: string = config.api.apiKey
): Promise<AnalystEstimate[]> {
  const req = makeFmpGetRequest(
    'analyst-estimates',
    { symbol, period, page: 0, limit },
    apiKey
  );
  if (!req) return [];
  try {
    const response = await fetch(req.url, req.init);
    if (!response.ok) {
      console.error('❌ Analyst Estimates API error:', response.status);
      return [];
    }
    const data: AnalystEstimateApiRow[] = await response.json();
    return (data ?? []).map((row) => ({
      symbol: row.symbol,
      date: row.date,
      estimatedRevenue: row.revenueAvg,
      estimatedRevenueLow: row.revenueLow,
      estimatedRevenueHigh: row.revenueHigh,
      estimatedNetIncome: row.netIncomeAvg,
      estimatedNetIncomeLow: row.netIncomeLow,
      estimatedNetIncomeHigh: row.netIncomeHigh,
      estimatedEps: row.epsAvg,
      estimatedEpsLow: row.epsLow,
      estimatedEpsHigh: row.epsHigh,
    }));
  } catch (error) {
    console.error('❌ Failed to fetch analyst estimates:', error);
    return [];
  }
}


// Type definitions for API response
export interface StockQuote {
  symbol: string;
  name: string;
  price: number;
  changesPercentage: number;
  change: number;
  dayLow: number;
  dayHigh: number;
  yearHigh: number;
  yearLow: number;
  marketCap: number;
  priceAvg50: number;
  priceAvg200: number;
  volume: number;
  avgVolume: number;
  exchange: string;
  open: number;
  previousClose: number;
  eps: number;
  pe: number;
  earningsAnnouncement: string;
  sharesOutstanding: number;
  timestamp: number;
}

export interface AfterHoursQuote {
  symbol: string;
  askPrice: number;
  askSize: number;
  bidPrice: number;
  bidSize: number;
  lastUpdateTime: string;
  lastPrice: number;
  lastSize: number;
}

export interface HistoricalPrice {
  date: string;
  close: number;
  price?: number; // Alias for close
  open?: number;
  high?: number;
  low?: number;
  volume?: number;
}

export interface EarningsDate {
  date: string;
}

export interface StockSearchResult {
  symbol: string;
  name: string;
  currency: string;
  stockExchange: string;
  exchangeShortName: string;
  exchange?: string;
  sector?: string;
  industry?: string;
}

export interface IncomeStatement {
  date: string;
  symbol: string;
  reportedCurrency: string;
  cik: string;
  filingDate: string;
  acceptedDate: string;
  fiscalYear: string;
  period: string;
  revenue: number;
  costOfRevenue: number;
  grossProfit: number;
  researchAndDevelopmentExpenses: number;
  generalAndAdministrativeExpenses: number;
  sellingAndMarketingExpenses: number;
  sellingGeneralAndAdministrativeExpenses: number;
  otherExpenses: number;
  operatingExpenses: number;
  costAndExpenses: number;
  netInterestIncome: number;
  interestIncome: number;
  interestExpense: number;
  depreciationAndAmortization: number;
  ebitda: number;
  ebit: number;
  nonOperatingIncomeExcludingInterest: number;
  operatingIncome: number;
  totalOtherIncomeExpensesNet: number;
  incomeBeforeTax: number;
  incomeTaxExpense: number;
  netIncomeFromContinuingOperations: number;
  netIncomeFromDiscontinuedOperations: number;
  otherAdjustmentsToNetIncome: number;
  netIncome: number;
  netIncomeDeductions: number;
  bottomLineNetIncome: number;
  eps: number;
  epsDiluted: number;
  weightedAverageShsOut: number;
  weightedAverageShsOutDil: number;
}

export interface BalanceSheet {
  date: string;
  symbol: string;
  reportedCurrency: string;
  cik: string;
  filingDate: string;
  acceptedDate: string;
  fiscalYear: string;
  period: string;
  cashAndCashEquivalents: number;
  shortTermInvestments: number;
  cashAndShortTermInvestments: number;
  netReceivables: number;
  inventory: number;
  otherCurrentAssets: number;
  totalCurrentAssets: number;
  propertyPlantEquipmentNet: number;
  goodwill: number;
  intangibleAssets: number;
  goodwillAndIntangibleAssets: number;
  longTermInvestments: number;
  taxAssets: number;
  otherNonCurrentAssets: number;
  totalNonCurrentAssets: number;
  otherAssets: number;
  totalAssets: number;
  accountPayables: number;
  otherPayables?: number;
  accruedExpenses?: number;
  shortTermDebt: number;
  taxPayables: number;
  deferredRevenue: number;
  otherCurrentLiabilities: number;
  totalCurrentLiabilities: number;
  longTermDebt: number;
  capitalLeaseObligationsNonCurrent?: number;
  deferredRevenueNonCurrent: number;
  deferredTaxLiabilitiesNonCurrent: number;
  otherNonCurrentLiabilities: number;
  totalNonCurrentLiabilities: number;
  otherLiabilities: number;
  capitalLeaseObligations: number;
  totalLiabilities: number;
  treasuryStock?: number;
  preferredStock: number;
  commonStock: number;
  retainedEarnings: number;
  additionalPaidInCapital?: number;
  accumulatedOtherComprehensiveIncomeLoss: number;
  // FMP field is `otherTotalStockholdersEquity`; keep legacy casing too.
  otherTotalStockholdersEquity?: number;
  othertotalStockholdersEquity: number;
  totalStockholdersEquity: number;
  totalEquity: number;
  totalLiabilitiesAndStockholdersEquity: number;
  minorityInterest: number;
  totalLiabilitiesAndTotalEquity: number;
  totalInvestments: number;
  totalDebt: number;
  netDebt: number;
}

export interface CashFlowStatement {
  date: string;
  symbol: string;
  reportedCurrency: string;
  cik: string;
  filingDate: string;
  acceptedDate: string;
  fiscalYear: string;
  period: string;
  netIncome: number;
  depreciationAndAmortization: number;
  deferredIncomeTax: number;
  stockBasedCompensation: number;
  changeInWorkingCapital: number;
  accountsReceivables: number;
  inventory: number;
  accountsPayables: number;
  otherWorkingCapital: number;
  changesInIncomeTaxes?: number;
  changesInDeferredRevenue?: number;
  otherNonCashItems: number;
  netCashProvidedByOperatingActivities: number;
  investmentsInPropertyPlantAndEquipment: number;
  acquisitionsNet: number;
  purchasesOfInvestments: number;
  salesMaturitiesOfInvestments: number;
  otherInvestingActivities: number;
  netCashUsedForInvestingActivities: number;
  debtRepayment: number;
  commonStockIssued: number;
  commonStockRepurchased: number;
  dividendsPaid: number;
  netDividendsPaid: number; // API uses this field
  commonDividendsPaid: number; // API uses this field for common stock dividends
  otherFinancingActivities: number;
  netCashUsedProvidedByFinancingActivities: number;
  effectOfForexChangesOnCash: number;
  netChangeInCash: number;
  cashAtEndOfPeriod: number;
  cashAtBeginningOfPeriod: number;
  operatingCashFlow: number;
  capitalExpenditure: number;
  freeCashFlow: number;
}

export interface KeyMetrics {
  date: string;
  symbol: string;
  period: string;
  revenuePerShare: number;
  netIncomePerShare: number;
  operatingCashFlowPerShare: number;
  freeCashFlowPerShare: number;
  cashPerShare: number;
  bookValuePerShare: number;
  tangibleBookValuePerShare: number;
  shareholdersEquityPerShare: number;
  interestDebtPerShare: number;
  marketCap: number;
  enterpriseValue: number;
  peRatio: number;
  priceToSalesRatio: number;
  pocfratio: number;
  pfcfRatio: number;
  pbRatio: number;
  ptbRatio: number;
  evToSales: number;
  enterpriseValueOverEBITDA: number;
  evToOperatingCashFlow: number;
  evToFreeCashFlow: number;
  earningsYield: number;
  freeCashFlowYield: number;
  debtToEquity: number;
  debtToAssets: number;
  netDebtToEBITDA: number;
  currentRatio: number;
  interestCoverage: number;
  incomeQuality: number;
  dividendYield: number;
  payoutRatio: number;
  salesGeneralAndAdministrativeToRevenue: number;
  researchAndDdevelopementToRevenue: number;
  intangiblesToTotalAssets: number;
  capexToOperatingCashFlow: number;
  capexToRevenue: number;
  capexToDepreciation: number;
  stockBasedCompensationToRevenue: number;
  grahamNumber: number;
  roic: number;
  returnOnInvestedCapital: number; // API uses this field name instead of roic
  returnOnTangibleAssets: number;
  grahamNetNet: number;
  workingCapital: number;
  tangibleAssetValue: number;
  netCurrentAssetValue: number;
  investedCapital: number;
  averageReceivables: number;
  averagePayables: number;
  averageInventory: number;
  daysSalesOutstanding: number;
  daysPayablesOutstanding: number;
  daysOfInventoryOnHand: number;
  receivablesTurnover: number;
  payablesTurnover: number;
  inventoryTurnover: number;
  roe: number;
  capexPerShare: number;
}

export interface Ratios {
  date: string;
  symbol: string;
  period?: string;
  // FMP ratios commonly provide margins as ratios (0.42) not percents.
  grossProfitMargin?: number;
  operatingProfitMargin?: number;
  effectiveTaxRate?: number;
  netProfitMargin?: number;
}

/**
 * Fetch income statement data from API
 * @param symbol - Stock symbol
 * @param period - 'annual' or 'quarter'
 * @param limit - Number of statements to fetch (default 15)
 * @param apiKey - API key
 * @returns Promise with income statement array
 */
export async function fetchIncomeStatement(
  symbol: string = config.api.defaultSymbol,
  period: 'annual' | 'quarter' = 'annual',
  limit: number = 15,
  apiKey: string = config.api.apiKey
): Promise<IncomeStatement[]> {
  const req = makeFmpGetRequest('income-statement', { symbol, period, limit }, apiKey);
  if (!req) return [];
  try {
    const response = await fetch(req.url, req.init);
    
    if (!response.ok) {
      console.error('❌ API error:', response.status);
      return [];
    }

    if (response.status === 204) return [];
    
    const raw: any = await response.json();
    const data: IncomeStatement[] = (Array.isArray(raw) ? raw : []).map(normalizeIncomeStatementRow);
    return data;
  } catch (error) {
    console.error('❌ Failed to fetch income statement:', error);
    return [];
  }
}

/**
 * Fetch trailing-twelve-months (TTM) income statement from API.
 * Uses the stable endpoint: /income-statement-ttm
 * @param symbol - Stock symbol
 * @param limit - Number of TTM records to fetch (default 1)
 * @param apiKey - API key
 * @returns Promise with the most recent TTM income statement row, or null
 */
export async function fetchIncomeStatementTTM(
  symbol: string = config.api.defaultSymbol,
  limit: number = 1,
  apiKey: string = config.api.apiKey
): Promise<IncomeStatement | null> {
  const req = makeFmpGetRequest('income-statement-ttm', { symbol, limit }, apiKey);
  if (!req) return null;
  try {
    const response = await fetch(req.url, req.init);
    if (!response.ok) {
      console.error('❌ Income Statement TTM API error:', response.status);
      return null;
    }

    if (response.status === 204) return null;

    const raw: any = await response.json();
    const data: IncomeStatement[] = (Array.isArray(raw) ? raw : []).map(normalizeIncomeStatementRow);
    if (data.length > 0) return data[0];
    return null;
  } catch (error) {
    console.error('❌ Failed to fetch income statement TTM:', error);
    return null;
  }
}

/**
 * Fetch trailing-twelve-months (TTM) income statement series from API (AS-IS).
 * Uses the stable endpoint: /income-statement-ttm
 * @param symbol - Stock symbol
 * @param limit - Number of TTM records to fetch (default 80)
 * @param apiKey - API key
 * @returns Promise with TTM income statement rows (oldest -> newest)
 */
export async function fetchIncomeStatementTTMSeries(
  symbol: string = config.api.defaultSymbol,
  limit: number = 80,
  apiKey: string = config.api.apiKey
): Promise<IncomeStatement[]> {
  const req = makeFmpGetRequest('income-statement-ttm', { symbol, limit }, apiKey);
  if (!req) return [];
  try {
    const response = await fetch(req.url, req.init);
    if (!response.ok) {
      console.error('❌ Income Statement TTM Series API error:', response.status);
      return [];
    }

    if (response.status === 204) return [];

    const raw: any = await response.json();
    const data: IncomeStatement[] = (Array.isArray(raw) ? raw : []).map(normalizeIncomeStatementRow);
    return data
      .filter((row) => row && typeof row.date === 'string')
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  } catch (error) {
    console.error('❌ Failed to fetch income statement TTM series:', error);
    return [];
  }
}

/**
 * Fetch financial ratios from API.
 * Uses the stable endpoint: /ratios
 * @param symbol - Stock symbol
 * @param period - 'annual' or 'quarter'
 * @param limit - Number of rows to fetch
 * @param apiKey - API key
 */
export async function fetchRatios(
  symbol: string = config.api.defaultSymbol,
  period: 'annual' | 'quarter' = 'annual',
  limit: number = 15,
  apiKey: string = config.api.apiKey
): Promise<Ratios[]> {
  const req = makeFmpGetRequest('ratios', { symbol, period, limit }, apiKey);
  if (!req) return [];
  try {
    const response = await fetch(req.url, req.init);
    if (!response.ok) {
      console.error('❌ Ratios API error:', response.status);
      return [];
    }

    if (response.status === 204) return [];

    const data: Ratios[] = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('❌ Failed to fetch ratios:', error);
    return [];
  }
}

/**
 * Fetch trailing-twelve-months (TTM) cash flow statement from API.
 * Uses the stable endpoint: /cash-flow-statement-ttm
 * @param symbol - Stock symbol
 * @param limit - Number of TTM records to fetch (default 1)
 * @param apiKey - API key
 * @returns Promise with the most recent TTM cash flow statement row, or null
 */
export async function fetchCashFlowStatementTTM(
  symbol: string = config.api.defaultSymbol,
  limit: number = 1,
  apiKey: string = config.api.apiKey
): Promise<CashFlowStatement | null> {
  const req = makeFmpGetRequest('cash-flow-statement-ttm', { symbol, limit }, apiKey);
  if (!req) return null;
  try {
    const response = await fetch(req.url, req.init);
    if (!response.ok) {
      console.error('❌ Cash Flow Statement TTM API error:', response.status);
      return null;
    }

    if (response.status === 204) return null;

    const raw: any = await response.json();
    const data: CashFlowStatement[] = (Array.isArray(raw) ? raw : []).map(normalizeCashFlowStatementRow);
    if (data.length > 0) return data[0];
    return null;
  } catch (error) {
    console.error('❌ Failed to fetch cash flow statement TTM:', error);
    return null;
  }
}

function normalizeCashFlowStatementRow(row: any): CashFlowStatement {
  if (!row || typeof row !== 'object') return row as CashFlowStatement;

  // Some FMP payloads use slightly different keys/typos. Normalize to our interface.
  if (row.accountsReceivables == null && row.accountsReceivable != null) row.accountsReceivables = row.accountsReceivable;
  if (row.accountsReceivables == null && row.accountReceivables != null) row.accountsReceivables = row.accountReceivables;

  // Common FMP typo seen in some versions: "Activites"
  if (row.netCashUsedForInvestingActivities == null && row.netCashUsedForInvestingActivites != null) {
    row.netCashUsedForInvestingActivities = row.netCashUsedForInvestingActivites;
  }
  if (row.netCashProvidedByOperatingActivities == null && row.netCashProvidedByOperatingActivites != null) {
    row.netCashProvidedByOperatingActivities = row.netCashProvidedByOperatingActivites;
  }
  if (row.netCashUsedProvidedByFinancingActivities == null && row.netCashUsedProvidedByFinancingActivites != null) {
    row.netCashUsedProvidedByFinancingActivities = row.netCashUsedProvidedByFinancingActivites;
  }

  // Alternate field names sometimes used by API/tooling.
  if (row.commonStockIssued == null && row.commonStockIssuance != null) row.commonStockIssued = row.commonStockIssuance;
  if (row.debtRepayment == null && row.netDebtIssuance != null) row.debtRepayment = row.netDebtIssuance;

  if (row.capitalExpenditure == null && row.capitalExpenditures != null) row.capitalExpenditure = row.capitalExpenditures;
  if (row.freeCashFlow == null && row.freeCashflow != null) row.freeCashFlow = row.freeCashflow;

  // Derive operatingCashFlow from the canonical operating line if missing.
  if (row.operatingCashFlow == null && row.netCashProvidedByOperatingActivities != null) {
    row.operatingCashFlow = row.netCashProvidedByOperatingActivities;
  }

  return coerceNumericFields(row, STRING_FIELDS) as CashFlowStatement;
}

const STRING_FIELDS = new Set([
  'symbol',
  'date',
  'reportedCurrency',
  'cik',
  'filingDate',
  'acceptedDate',
  'fiscalYear',
  'period',
  'link',
  'finalLink',
  'currency',
  'stockExchange',
  'exchangeShortName',
]);

function coerceNumericFields<T extends Record<string, any>>(row: T, stringKeys: Set<string>): T {
  if (!row || typeof row !== 'object') return row;
  const out: Record<string, any> = { ...row };
  for (const [key, value] of Object.entries(out)) {
    if (stringKeys.has(key)) continue;
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    const normalized = trimmed.replace(/,/g, '');
    const num = Number(normalized);
    if (Number.isFinite(num)) out[key] = num;
  }
  return out as T;
}

function normalizeIncomeStatementRow(row: any): IncomeStatement {
  if (!row || typeof row !== 'object') return row as IncomeStatement;
  return coerceNumericFields(row, STRING_FIELDS) as IncomeStatement;
}

function normalizeKeyMetricsRow(row: any): KeyMetrics {
  if (!row || typeof row !== 'object') return row as KeyMetrics;
  return coerceNumericFields(row, STRING_FIELDS) as KeyMetrics;
}

/**
 * Fetch trailing-twelve-months (TTM) cash flow statement series from API (AS-IS).
 * Uses the stable endpoint: /cash-flow-statement-ttm
 * @param symbol - Stock symbol
 * @param limit - Number of TTM records to fetch (default 120)
 * @param apiKey - API key
 * @returns Promise with TTM cash flow rows (oldest -> newest)
 */
export async function fetchCashFlowStatementTTMSeries(
  symbol: string = config.api.defaultSymbol,
  limit: number = 120,
  apiKey: string = config.api.apiKey
): Promise<CashFlowStatement[]> {
  const req = makeFmpGetRequest('cash-flow-statement-ttm', { symbol, limit }, apiKey);
  if (!req) return [];
  try {
    const response = await fetch(req.url, req.init);
    if (!response.ok) {
      console.error('❌ Cash Flow Statement TTM Series API error:', response.status);
      return [];
    }

    if (response.status === 204) return [];

    const raw: any = await response.json();
    const data: CashFlowStatement[] = (Array.isArray(raw) ? raw : []).map(normalizeCashFlowStatementRow);
    return data
      .filter((row) => row && typeof (row as any).date === 'string')
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  } catch (error) {
    console.error('❌ Failed to fetch cash flow statement TTM series:', error);
    return [];
  }
}

/**
 * Fetch earnings calendar date - tries multiple endpoints
 * @param symbol - Stock symbol
 * @param apiKey - API key
 * @returns Promise with earnings date
 */
export async function fetchEarningsDate(
  symbol: string = config.api.defaultSymbol,
  apiKey: string = config.api.apiKey
): Promise<string | null> {
  // Try multiple endpoints to find earnings data
  const endpoints: Array<{ path: string; query: Record<string, any> }> = [
    { path: 'stock-earnings-calendar', query: { symbol } },
    { path: `earning-calendar/${symbol}`, query: {} },
    { path: 'calendar/earning-calendar', query: { symbol } },
    { path: `company-earnings/${symbol}`, query: {} },
  ];
  for (const ep of endpoints) {
    try {
      const req = makeFmpGetRequest(ep.path, ep.query, apiKey);
      if (!req) return null;
      const response = await fetch(req.url, req.init);
      
      if (response.ok) {
        const data = await response.json();
        // Try different response formats
        if (Array.isArray(data) && data.length > 0) {
          if (data[0].date) return data[0].date;
          if (data[0].earningsDate) return data[0].earningsDate;
          if (data[0].reportDate) return data[0].reportDate;
        } else if (data && typeof data === 'object') {
          if (data.date) return data.date;
          if (data.earningsDate) return data.earningsDate;
          if (data.reportDate) return data.reportDate;
        }
      } else {
      }
    } catch (error) {
        }
  }
  
  return null;
}

/**
 * Fetch real-time stock quote from Financial Modeling Prep API
 * @param symbol - Stock symbol (e.g., 'AAPL')
 * @param apiKey - API key (optional, will use config default if not provided)
 * @returns Promise with stock quote data
 */
export async function fetchStockQuote(
  symbol: string = config.api.defaultSymbol,
  apiKey: string = config.api.apiKey
): Promise<StockQuote | null> {
  const normalizeQuote = (row: any): StockQuote | null => {
    if (!row || typeof row !== 'object') return null;
    if (typeof row.symbol !== 'string' || row.symbol.trim().length === 0) return null;

    // FMP stable endpoints sometimes use changePercentage (singular) and omit many fields.
    const changesPercentage =
      typeof row.changesPercentage === 'number'
        ? row.changesPercentage
        : (typeof row.changePercentage === 'number' ? row.changePercentage : 0);

    return {
      symbol: String(row.symbol),
      name: typeof row.name === 'string' ? row.name : '',
      price: typeof row.price === 'number' ? row.price : 0,
      changesPercentage,
      change: typeof row.change === 'number' ? row.change : 0,
      dayLow: typeof row.dayLow === 'number' ? row.dayLow : 0,
      dayHigh: typeof row.dayHigh === 'number' ? row.dayHigh : 0,
      yearHigh: typeof row.yearHigh === 'number' ? row.yearHigh : 0,
      yearLow: typeof row.yearLow === 'number' ? row.yearLow : 0,
      marketCap: typeof row.marketCap === 'number' ? row.marketCap : 0,
      priceAvg50: typeof row.priceAvg50 === 'number' ? row.priceAvg50 : 0,
      priceAvg200: typeof row.priceAvg200 === 'number' ? row.priceAvg200 : 0,
      volume: typeof row.volume === 'number' ? row.volume : 0,
      avgVolume: typeof row.avgVolume === 'number' ? row.avgVolume : 0,
      exchange: typeof row.exchange === 'string' ? row.exchange : '',
      open: typeof row.open === 'number' ? row.open : 0,
      previousClose: typeof row.previousClose === 'number' ? row.previousClose : 0,
      eps: typeof row.eps === 'number' ? row.eps : 0,
      pe: typeof row.pe === 'number' ? row.pe : 0,
      earningsAnnouncement: typeof row.earningsAnnouncement === 'string' ? row.earningsAnnouncement : '',
      sharesOutstanding: typeof row.sharesOutstanding === 'number' ? row.sharesOutstanding : 0,
      timestamp: typeof row.timestamp === 'number' ? row.timestamp : 0,
    };
  };

  const extractErrorMessage = (data: any): string | null => {
    if (!data || typeof data !== 'object') return null;
    const msg = (data['Error Message'] ?? data['error'] ?? data['message']) as unknown;
    return typeof msg === 'string' && msg.trim().length > 0 ? msg : null;
  };

  try {
    const candidates = [
      makeFmpGetRequest('batch-quote', { symbols: symbol }, apiKey),
      makeFmpGetRequest('quote', { symbol }, apiKey),
    ].filter((x): x is FmpGetRequest => Boolean(x));

    if (candidates.length === 0) return null;

    for (const req of candidates) {
      const response = await fetch(req.url, req.init);
      if (!response.ok) {
        console.error(`Quote API HTTP ${response.status} (${req.url})`);
        continue;
      }

      const data = await response.json();

      // Most endpoints return an array.
      if (Array.isArray(data) && data.length > 0) {
        const q = normalizeQuote(data[0]);
        if (q) return q;
      }

      // Some variants can return a single object.
      const q = normalizeQuote(data);
      if (q) return q;

      const err = extractErrorMessage(data);
      if (err) {
        console.error(`Quote API error: ${err}`);
        return null;
      }
    }

    console.error('Quote API: no data returned');
    return null;
  } catch (error) {
    console.error('Error fetching stock quote:', error);
    return null;
  }
}

/**
 * Fetch after hours quote from Financial Modeling Prep API
 * Uses the aftermarket-quote endpoint for post-market trading data
 * @param symbol - Stock symbol (e.g., 'AAPL')
 * @param apiKey - API key (optional, will use config default if not provided)
 * @returns Promise with after hours quote data
 */
export async function fetchAfterHoursQuote(
  symbol: string = config.api.defaultSymbol,
  apiKey: string = config.api.apiKey
): Promise<AfterHoursQuote | null> {
  try {
    const req = makeFmpGetRequest('aftermarket-quote', { symbol }, apiKey);
    if (!req) return null;
    const response = await fetch(req.url, req.init);
    
    if (!response.ok) {
      console.error(`HTTP error! status: ${response.status}`);
      return null;
    }
    
    const data = await response.json();
    const afterHoursData = Array.isArray(data) ? data[0] : data;
    if (afterHoursData && typeof afterHoursData === 'object') {
      // Calculate mid price from bid and ask
      const askPrice = afterHoursData.askPrice || 0;
      const bidPrice = afterHoursData.bidPrice || 0;
      const midPrice = (askPrice + bidPrice) / 2;
      const result = {
        symbol: afterHoursData.symbol || symbol,
        askPrice: askPrice,
        askSize: afterHoursData.askSize || 0,
        bidPrice: bidPrice,
        bidSize: afterHoursData.bidSize || 0,
        lastUpdateTime: new Date(afterHoursData.timestamp).toISOString(),
        lastPrice: midPrice, // Use mid price between bid and ask
        lastSize: afterHoursData.volume || 0
      };
      return result;
    }
    
      
    return null;
  } catch (error) {
    console.error('Error fetching after hours quote:', error);
    return null;
  }
}

/**
 * Get stock quote - either from API or mock data based on config
 * @param symbol - Stock symbol
 * @returns Promise with stock quote data
 */
export async function getStockQuote(symbol: string = config.api.defaultSymbol): Promise<StockQuote | null> {
  return await fetchStockQuote(symbol);
}

/**
 * Get after hours quote
 * @param symbol - Stock symbol
 * @returns Promise with after hours quote data
 */
export async function getAfterHoursQuote(symbol: string = config.api.defaultSymbol): Promise<AfterHoursQuote | null> {
  // Try to get data from aftermarket-quote API
  const data = await fetchAfterHoursQuote(symbol);

  if (data && data.lastPrice > 0) {
    return data;
  }

  // If aftermarket-quote doesn't return data, calculate from batch-quote
  // This happens when market is open or no after-hours trading yet
  const currentQuote = await fetchStockQuote(symbol);

  if (currentQuote && currentQuote.price > 0) {
    // Use current price as after hours price (0% change during market hours)
    return {
      symbol: currentQuote.symbol,
      askPrice: currentQuote.price,
      askSize: 0,
      bidPrice: currentQuote.price,
      bidSize: 0,
      lastUpdateTime: new Date(currentQuote.timestamp * 1000).toISOString(),
      lastPrice: currentQuote.price,
      lastSize: 0
    };
  }

  // Both endpoints failed (or key missing)
  return null;
}

/**
 * Get mock stock quote data
 * @param symbol - Stock symbol
 * @returns Mock stock quote data
 */
function getMockStockQuote(symbol: string): StockQuote {
  // This data structure matches the API response
  return {
    symbol: symbol,
    name: 'Alphabet Inc.',
    price: 267.46,
    changesPercentage: 0.75,
    change: 2.00,
    dayLow: 265.00,
    dayHigh: 268.50,
    yearHigh: 280.00,
    yearLow: 200.00,
    marketCap: 1750000000000,
    priceAvg50: 260.00,
    priceAvg200: 250.00,
    volume: 25000000,
    avgVolume: 23000000,
    exchange: 'NASDAQ',
    open: 266.00,
    previousClose: 265.46,
    eps: 10.50,
    pe: 25.47,
    earningsAnnouncement: '2028-12-12T16:00:00.000+0000',
    sharesOutstanding: 6500000000,
    timestamp: Date.now()
  };
}

/**
 * Get mock after hours quote data
 * @param symbol - Stock symbol
 * @param currentPrice - Current market price to calculate after hours from
 * @returns Mock after hours quote data
 */
function getMockAfterHoursQuote(symbol: string, currentPrice: number): AfterHoursQuote {
  // After-hours price: currentPrice + 0.087% (about $0.23 on average)
  const afterHoursIncrease = 0.00087; // 0.087%
  const afterHoursPrice = currentPrice * (1 + afterHoursIncrease);
  
  return {
    symbol: symbol,
    askPrice: afterHoursPrice + 0.01,
    askSize: 100,
    bidPrice: afterHoursPrice - 0.01,
    bidSize: 100,
    lastUpdateTime: new Date().toISOString(),
    lastPrice: afterHoursPrice,
    lastSize: 50
  };
}

/**
 * Fetch historical price data for mini chart (last 30 days)
 * @param symbol - Stock symbol (e.g., 'AAPL')
 * @param apiKey - API key (optional, will use config default if not provided)
 * @returns Promise with historical prices
 */
const historicalPricesCache = new Map<string, { expiresAt: number; data: HistoricalPrice[] }>();
const historicalPricesInFlight = new Map<string, Promise<HistoricalPrice[]>>();
const HISTORICAL_PRICES_CACHE_MS = 5 * 60 * 1000;

export async function fetchHistoricalPrices(
  symbol: string = config.api.defaultSymbol,
  apiKey: string = config.api.apiKey
): Promise<HistoricalPrice[]> {
  const normalizedSymbol = String(symbol || config.api.defaultSymbol || '').trim().toUpperCase();
  const toDate = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const cacheKey = `${normalizedSymbol}|${toDate}|${apiKey || 'default'}`;
  const now = Date.now();
  const cached = historicalPricesCache.get(cacheKey);
  if (cached && cached.expiresAt > now) return [...cached.data];

  const inFlight = historicalPricesInFlight.get(cacheKey);
  if (inFlight) return inFlight.then((data) => [...data]);

  const requestPromise = (async (): Promise<HistoricalPrice[]> => {
    try {
      // Use historical-price-eod API endpoint for long-term daily data.
      // Using a hard-coded `to` date can make the charts appear "stuck" in the past.
      // Start far enough back to capture true all-time lows/highs for mature tickers (e.g., MSFT).
      const fromDate = '1980-01-01';
      const req = makeFmpGetRequest(
        'historical-price-eod/full',
        { symbol: normalizedSymbol, from: fromDate, to: toDate },
        apiKey
      );
      if (!req) return [];
      const response = await fetch(req.url, req.init);
      
      if (!response.ok) {
        console.error('❌ HTTP error! status:', response.status);
        return [];
      }
      
      const data = await response.json();
      // Handle different response formats
      let pricesArray: any[] = [];
      
      if (Array.isArray(data)) {
        pricesArray = data;
      } else if (data && data.historical && Array.isArray(data.historical)) {
        pricesArray = data.historical;
      } else if (data && data.prices && Array.isArray(data.prices)) {
        pricesArray = data.prices;
      } else {
        console.warn('⚠️ Unexpected data format:', data);
        return [];
      }
      
      if (pricesArray.length === 0) {
        console.warn('⚠️ No price data found in response');
        return [];
      }
      
      // Map to HistoricalPrice format
      let allPrices: HistoricalPrice[] = pricesArray.map((item: any) => ({
        date: item.date,
        close: item.close || item.price,
        price: item.close || item.price,
        open: typeof item.open === 'number' ? item.open : undefined,
        high: typeof item.high === 'number' ? item.high : undefined,
        low: typeof item.low === 'number' ? item.low : undefined,
        volume: typeof item.volume === 'number' ? item.volume : undefined,
      }));
      
      // Sort by date (oldest first)
      allPrices.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      
      // Return all daily data (no weekly filtering)
      historicalPricesCache.set(cacheKey, { expiresAt: Date.now() + HISTORICAL_PRICES_CACHE_MS, data: allPrices });
      return allPrices;
    } catch (error) {
      console.error('❌ Error in fetchHistoricalPrices:', error);
      return [];
    } finally {
      historicalPricesInFlight.delete(cacheKey);
    }
  })();

  historicalPricesInFlight.set(cacheKey, requestPromise);
  return requestPromise.then((data) => [...data]);
}

export interface IntradayPrice {
  date: string; // e.g. "2026-01-17 15:55:00"
  close: number;
  price: number;
  open?: number;
  high?: number;
  low?: number;
  volume?: number;
}

/**
 * Fetch intraday price data for the current trading day.
 * Uses FMP endpoint (stable): /historical-chart/5min?symbol={symbol}
 */
export async function fetchIntradayPrices(
  symbol: string = config.api.defaultSymbol,
  apiKey: string = config.api.apiKey
): Promise<IntradayPrice[]> {
  try {
    // FMP /stable expects symbol as a query param (path form returns 404 []).
    // Keep a minimal fallback for older setups.
    const candidates = [
      makeFmpGetRequest('historical-chart/5min', { symbol }, apiKey),
      // Fallback: some older code/examples use path param (often 404 on /stable).
      makeFmpGetRequest(`historical-chart/5min/${symbol}`, {}, apiKey),
    ].filter((x): x is FmpGetRequest => Boolean(x));

    if (candidates.length === 0) return [];

    let lastStatus: number | null = null;
    let data: unknown = null;

    for (const req of candidates) {
      const response = await fetch(req.url, req.init);
      lastStatus = response.status;

      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem('intraday:lastStatus', String(response.status));
        } catch {
          // ignore
        }
      }

      if (!response.ok) {
        // Try next candidate before giving up.
        continue;
      }

      data = await response.json();
      break;
    }

    if (!Array.isArray(data) || data.length === 0) {
      if (lastStatus != null && lastStatus !== 200) {
        console.error('❌ Intraday HTTP error! status:', lastStatus);
      }
      return [];
    }

    const points: IntradayPrice[] = (data as any[])
      .map((item: any) => {
        const close = typeof item?.close === 'number' ? item.close : Number(item?.close);
        if (!Number.isFinite(close)) return null;
        return {
          date: String(item?.date ?? ''),
          close,
          price: close,
          open: typeof item?.open === 'number' ? item.open : undefined,
          high: typeof item?.high === 'number' ? item.high : undefined,
          low: typeof item?.low === 'number' ? item.low : undefined,
          volume: typeof item?.volume === 'number' ? item.volume : undefined,
        };
      })
      .filter(Boolean) as IntradayPrice[];

    // Sort oldest first. FMP often returns newest first.
    points.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return points;
  } catch (error) {
    console.error('❌ Error in fetchIntradayPrices:', error);
    return [];
  }
}

/**
 * Fetch balance sheet data from API
 * @param symbol - Stock symbol
 * @param period - 'annual' or 'quarter'
 * @param limit - Number of statements to fetch (default 15)
 * @param apiKey - API key
 * @returns Promise with balance sheet array
 */
export async function fetchBalanceSheet(
  symbol: string = config.api.defaultSymbol,
  period: 'annual' | 'quarter' = 'annual',
  limit: number = 15,
  apiKey: string = config.api.apiKey
): Promise<BalanceSheet[]> {
  const req = makeFmpGetRequest('balance-sheet-statement', { symbol, period, limit }, apiKey);
  if (!req) return [];
  try {
    const response = await fetch(req.url, req.init);
    
    if (!response.ok) {
      console.error('❌ API error:', response.status);
      return [];
    }

    if (response.status === 204) return [];
    
    const data: BalanceSheet[] = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Failed to fetch balance sheet:', error);
    return [];
  }
}

/**
 * Fetch trailing-twelve-months (TTM) balance sheet statement from API.
 * Uses the stable endpoint: /balance-sheet-statement-ttm
 * @param symbol - Stock symbol
 * @param limit - Number of TTM records to fetch (default 1)
 * @param apiKey - API key
 * @returns Promise with the most recent TTM balance sheet row, or null
 */
export async function fetchBalanceSheetTTM(
  symbol: string = config.api.defaultSymbol,
  limit: number = 1,
  apiKey: string = config.api.apiKey
): Promise<BalanceSheet | null> {
  const req = makeFmpGetRequest('balance-sheet-statement-ttm', { symbol, limit }, apiKey);
  if (!req) return null;
  try {
    const response = await fetch(req.url, req.init);

    if (!response.ok) {
      console.error('❌ Balance Sheet TTM API error:', response.status);
      return null;
    }

    if (response.status === 204) return null;

    const data: BalanceSheet[] = await response.json();
    if (Array.isArray(data) && data.length > 0) return data[0];
    return null;
  } catch (error) {
    console.error('❌ Failed to fetch balance sheet TTM:', error);
    return null;
  }
}

/**
 * Fetch cash flow statement data from API
 * @param symbol - Stock symbol
 * @param period - 'annual' or 'quarter'
 * @param limit - Number of statements to fetch (default 15)
 * @param apiKey - API key
 * @returns Promise with cash flow statement array
 */
export async function fetchCashFlowStatement(
  symbol: string = config.api.defaultSymbol,
  period: 'annual' | 'quarter' = 'annual',
  limit: number = 15,
  apiKey: string = config.api.apiKey
): Promise<CashFlowStatement[]> {
  const req = makeFmpGetRequest('cash-flow-statement', { symbol, period, limit }, apiKey);
  if (!req) return [];
  try {
    const response = await fetch(req.url, req.init);
    
    if (!response.ok) {
      console.error('❌ API error:', response.status);
      return [];
    }

    if (response.status === 204) return [];
    
    const raw: any = await response.json();
    const data: CashFlowStatement[] = (Array.isArray(raw) ? raw : []).map(normalizeCashFlowStatementRow);
    return data;
  } catch (error) {
    console.error('❌ Failed to fetch cash flow statement:', error);
    return [];
  }
}
/**
 * Fetch quarterly cash flow statements (raw per-quarter rows, not TTM).
 * This keeps a stable explicit API for callers that need filing-date aligned quarter anchors.
 */
export async function fetchQuarterlyCashFlowStatements(
  symbol: string = config.api.defaultSymbol,
  limit: number = 120,
  apiKey: string = config.api.apiKey
): Promise<CashFlowStatement[]> {
  return fetchCashFlowStatement(symbol, 'quarter', limit, apiKey);
}

/**
 * Fetch quotes for multiple symbols in a single batch request.
 * Returns a map of symbol -> StockQuote for each symbol that was found.
 */
export async function fetchBatchStockQuotes(
  symbols: string[],
  apiKey: string = config.api.apiKey
): Promise<Record<string, StockQuote>> {
  if (symbols.length === 0) return {};
  const normalizeQuote = (row: any): StockQuote | null => {
    if (!row || typeof row !== 'object') return null;
    if (typeof row.symbol !== 'string' || row.symbol.trim().length === 0) return null;
    const changesPercentage =
      typeof row.changesPercentage === 'number'
        ? row.changesPercentage
        : (typeof row.changePercentage === 'number' ? row.changePercentage : 0);
    return {
      symbol: String(row.symbol),
      name: typeof row.name === 'string' ? row.name : '',
      price: typeof row.price === 'number' ? row.price : 0,
      changesPercentage,
      change: typeof row.change === 'number' ? row.change : 0,
      dayLow: typeof row.dayLow === 'number' ? row.dayLow : 0,
      dayHigh: typeof row.dayHigh === 'number' ? row.dayHigh : 0,
      yearHigh: typeof row.yearHigh === 'number' ? row.yearHigh : 0,
      yearLow: typeof row.yearLow === 'number' ? row.yearLow : 0,
      marketCap: typeof row.marketCap === 'number' ? row.marketCap : 0,
      priceAvg50: typeof row.priceAvg50 === 'number' ? row.priceAvg50 : 0,
      priceAvg200: typeof row.priceAvg200 === 'number' ? row.priceAvg200 : 0,
      volume: typeof row.volume === 'number' ? row.volume : 0,
      avgVolume: typeof row.avgVolume === 'number' ? row.avgVolume : 0,
      exchange: typeof row.exchange === 'string' ? row.exchange : '',
      open: typeof row.open === 'number' ? row.open : 0,
      previousClose: typeof row.previousClose === 'number' ? row.previousClose : 0,
      eps: typeof row.eps === 'number' ? row.eps : 0,
      pe: typeof row.pe === 'number' ? row.pe : 0,
      earningsAnnouncement: typeof row.earningsAnnouncement === 'string' ? row.earningsAnnouncement : '',
      sharesOutstanding: typeof row.sharesOutstanding === 'number' ? row.sharesOutstanding : 0,
      timestamp: typeof row.timestamp === 'number' ? row.timestamp : 0,
    };
  };

  try {
    const req = makeFmpGetRequest('batch-quote', { symbols: symbols.join(',') }, apiKey);
    if (!req) return {};
    const response = await fetch(req.url, req.init);
    if (!response.ok) return {};
    const data = await response.json();
    if (!Array.isArray(data)) return {};
    const result: Record<string, StockQuote> = {};
    for (const row of data) {
      const q = normalizeQuote(row);
      if (q) result[q.symbol] = q;
    }
    return result;
  } catch {
    return {};
  }
}

/**
 * Fetch key metrics data from API
 * @param symbol - Stock symbol
 * @param period - 'annual' or 'quarter'
 * @param limit - Number of statements to fetch (default 15)
 * @param apiKey - API key
 * @returns Promise with key metrics array
 */
export async function fetchKeyMetrics(
  symbol: string = config.api.defaultSymbol,
  period: 'annual' | 'quarter' = 'annual',
  limit: number = 15,
  apiKey: string = config.api.apiKey
): Promise<KeyMetrics[]> {
  const req = makeFmpGetRequest('key-metrics', { symbol, period, limit }, apiKey);
  if (!req) return [];
  try {
    const response = await fetch(req.url, req.init);
    
    if (!response.ok) {
      console.error('❌ API error:', response.status);
      return [];
    }

    if (response.status === 204) return [];
    
    const raw: any = await response.json();
    const data: KeyMetrics[] = (Array.isArray(raw) ? raw : []).map(normalizeKeyMetricsRow);
    return data;
  } catch (error) {
    console.error('❌ Failed to fetch key metrics:', error);
    return [];
  }
}

/**
 * Fetch trailing-twelve-months (TTM) key metrics from API.
 * Uses the stable endpoint: /key-metrics-ttm
 * @param symbol - Stock symbol
 * @param limit - Number of rows to fetch (default 1)
 * @param apiKey - API key
 * @returns Promise with the most recent TTM key metrics row, or null
 */
export async function fetchKeyMetricsTTM(
  symbol: string = config.api.defaultSymbol,
  limit: number = 1,
  apiKey: string = config.api.apiKey
): Promise<KeyMetrics | null> {
  const req = makeFmpGetRequest('key-metrics-ttm', { symbol, limit }, apiKey);
  if (!req) {
    // No dedicated local TTM key-metrics source; callers can fall back to
    // derived metrics from local TTM statements if desired.
    return null;
  }
  try {
    const response = await fetch(req.url, req.init);
    if (!response.ok) {
      console.error('❌ Key Metrics TTM API error:', response.status);
      return null;
    }

    if (response.status === 204) return null;

    const raw: any = await response.json();
    const data: KeyMetrics[] = (Array.isArray(raw) ? raw : []).map(normalizeKeyMetricsRow);
    if (data.length > 0) return data[0];
    return null;
  } catch (error) {
    console.error('❌ Failed to fetch key metrics TTM:', error);
    return null;
  }
}

/**
 * Search for stocks by symbol or company name (autocomplete)
 * @param query - Search query (e.g., 'AAPL', 'Apple', 'MSFT')
 * @param limit - Maximum number of results (default 10)
 * @param apiKey - API key (optional, will use config default if not provided)
 * @returns Promise with array of stock search results
 */
export async function searchStocks(
  query: string,
  limit: number = 10,
  apiKey: string = config.api.apiKey
): Promise<StockSearchResult[]> {
  if (!query || query.trim().length < 1) {
    return [];
  }

  try {
    const req = makeFmpGetRequest('search-symbol', { query, limit }, apiKey);
    if (!req) return [];
    const response = await fetch(req.url, req.init);
    if (!response.ok) {
      if (response.status === 404) return [];
      console.error('❌ Search API error:', response.status);
      return [];
    }
    const data: StockSearchResult[] = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Error searching stocks:', error);
    return [];
  }
}
